import { useEffect, useState, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
  Loader2, Gift, Plane, GraduationCap, Heart, ArrowRight,
  TrendingUp, Calendar, CreditCard, Users, AlertTriangle,
} from "lucide-react";
import { Link } from "react-router-dom";
import { format, addDays, isBefore, parseISO } from "date-fns";
import ReferralShare from "@/components/ReferralShare";
import { toast } from "sonner";

/* ─── Tier colours ─── */
const tierColors: Record<string, { bg: string; text: string }> = {
  bronze: { bg: "bg-[hsl(30,50%,50%)]", text: "text-[hsl(0,0%,100%)]" },
  silver: { bg: "bg-[hsl(0,0%,70%)]", text: "text-[hsl(240,27%,14%)]" },
  gold: { bg: "bg-[hsl(var(--flexaura-accent))]", text: "text-[hsl(0,0%,100%)]" },
};

const tierThresholds: Record<string, number> = { bronze: 0, silver: 1000, gold: 5000 };

const statusPill: Record<string, string> = {
  confirmed: "bg-[hsl(var(--flexaura-success))] text-[hsl(0,0%,100%)]",
  pending: "bg-[hsl(var(--warning))] text-[hsl(240,27%,14%)]",
  cancelled: "bg-[hsl(var(--flexaura-error))] text-[hsl(0,0%,100%)]",
  completed: "bg-[hsl(var(--flexaura-primary))] text-[hsl(0,0%,100%)]",
  initiated: "bg-[hsl(var(--warning))] text-[hsl(240,27%,14%)]",
};

const verticalIcon: Record<string, string> = {
  travel: "✈️",
  education: "🎓",
  healthcare: "🏥",
};

/* ─── Points filter ─── */
type PointsFilter = "all" | "earn" | "redeem" | "expire" | "referral";

interface PointsRow {
  id: string;
  kind: string;
  source: string;
  description: string | null;
  points: number;
  points_sign: number;
  created_at: string;
  expires_at: string | null;
}

interface BookingRow {
  id: string;
  offer_id: string;
  status: string;
  total_amount_aed: number;
  created_at: string | null;
  pnr: string | null;
}

export default function Dashboard() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);

  // Overview data
  const [displayName, setDisplayName] = useState("");
  const [tier, setTier] = useState("bronze");
  const [availablePoints, setAvailablePoints] = useState(0);
  const [tierPoints, setTierPoints] = useState(0);
  const [nextTierMin, setNextTierMin] = useState(1000);

  // Bookings
  const [bookings, setBookings] = useState<BookingRow[]>([]);

  // Points ledger
  const [ledger, setLedger] = useState<PointsRow[]>([]);
  const [pointsFilter, setPointsFilter] = useState<PointsFilter>("all");

  // Stats
  const [totalFinanced, setTotalFinanced] = useState(0);
  const [expiringIn30, setExpiringIn30] = useState(0);
  const [referralCount, setReferralCount] = useState(0);

  // Preferences
  const [preferredBnpl, setPreferredBnpl] = useState<string | null>(null);
  const [notifyEarned, setNotifyEarned] = useState(true);
  const [notifyExpiring, setNotifyExpiring] = useState(true);

  // Redeem modal
  const [redeemOpen, setRedeemOpen] = useState(false);
  const [redeemAmount, setRedeemAmount] = useState(500);

  useEffect(() => {
    if (!user) return;
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  async function loadAll() {
    setLoading(true);
    try {
      await Promise.all([
        loadUserPoints(),
        loadBookings(),
        loadLedger(),
        loadPreferences(),
        loadReferralCount(),
      ]);
    } finally {
      setLoading(false);
    }
  }

  async function loadUserPoints() {
    const { data } = await supabase.rpc("get_user_points", { p_user_id: user!.id });
    if (data && data.length > 0) {
      const row = data[0] as any;
      setAvailablePoints(row.available_points ?? 0);
      setTier(row.tier ?? "bronze");
      setTierPoints(row.tier_points ?? 0);
      setNextTierMin(row.next_tier_min ?? 5000);
    }

    // Display name
    const { data: u } = await supabase
      .from("users")
      .select("display_name, full_name")
      .eq("id", user!.id)
      .single();
    if (u) {
      const name = u.display_name || u.full_name || "";
      setDisplayName(name.split(" ")[0] || "Member");
    }
  }

  async function loadBookings() {
    const { data } = await supabase
      .from("bookings")
      .select("id, offer_id, status, total_amount_aed, created_at, pnr")
      .eq("user_id", user!.id)
      .order("created_at", { ascending: false })
      .limit(10);
    const rows = (data ?? []) as BookingRow[];
    setBookings(rows);
    setTotalFinanced(rows.reduce((s, b) => s + Number(b.total_amount_aed || 0), 0));
  }

  async function loadLedger() {
    const { data } = await supabase
      .from("points_ledger")
      .select("id, kind, source, description, points, points_sign, created_at, expires_at")
      .eq("user_id", user!.id)
      .order("created_at", { ascending: false })
      .limit(50);
    const rows = (data ?? []) as PointsRow[];
    setLedger(rows);

    // Count points expiring within 30 days
    const cutoff = addDays(new Date(), 30);
    const expiring = rows
      .filter(
        (r) =>
          r.kind === "earn" &&
          r.points_sign === 1 &&
          r.expires_at &&
          isBefore(parseISO(r.expires_at), cutoff)
      )
      .reduce((s, r) => s + r.points, 0);
    setExpiringIn30(expiring);
  }

  async function loadPreferences() {
    const { data } = await supabase
      .from("user_preferences" as any)
      .select("*")
      .eq("user_id", user!.id)
      .maybeSingle();
    if (data) {
      setPreferredBnpl((data as any).preferred_bnpl_provider);
      setNotifyEarned((data as any).notify_points_earned ?? true);
      setNotifyExpiring((data as any).notify_points_expiring ?? true);
    }
  }

  async function loadReferralCount() {
    const { count } = await supabase
      .from("referrals")
      .select("id", { count: "exact", head: true })
      .eq("referrer_user_id", user!.id)
      .not("referred_user_id", "is", null);
    setReferralCount(count ?? 0);
  }

  async function savePreference(field: string, value: any) {
    const payload = { user_id: user!.id, [field]: value, updated_at: new Date().toISOString() };
    await supabase.from("user_preferences" as any).upsert(payload as any, { onConflict: "user_id" });
  }

  async function handleRedeem() {
    try {
      const { error } = await supabase.rpc("redeem_points", {
        p_user: user!.id,
        p_points: redeemAmount,
        p_desc: "Dashboard redemption",
      });
      if (error) throw error;
      toast.success(`Redeemed ${redeemAmount} points (AED ${(redeemAmount / 100).toFixed(2)} credit)`);
      setRedeemOpen(false);
      loadAll();
    } catch (e: any) {
      toast.error(e.message || "Redemption failed");
    }
  }

  // Filtered ledger
  const filteredLedger = useMemo(() => {
    if (pointsFilter === "all") return ledger;
    if (pointsFilter === "referral") return ledger.filter((r) => r.source === "referral");
    return ledger.filter((r) => r.kind === pointsFilter);
  }, [ledger, pointsFilter]);

  // Tier progress
  const currentTierMin = tierThresholds[tier] ?? 0;
  const progressRange = nextTierMin - currentTierMin;
  const tierProgress = progressRange > 0 ? Math.min(100, ((tierPoints - currentTierMin) / progressRange) * 100) : 100;
  const pointsToNext = Math.max(0, nextTierMin - tierPoints);
  const nextTierName = tier === "bronze" ? "Silver" : tier === "silver" ? "Gold" : "Gold";

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* ═══ 1. DASHBOARD HEADER ═══ */}
      <div className="rounded-xl bg-[hsl(var(--flexaura-primary-dark))] p-6 md:p-8">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-heading text-[hsl(0,0%,100%)]">
              Welcome back, {displayName}
            </h1>
            <p className="font-mono text-sm text-[hsl(0,0%,100%,0.5)] mt-1">
              {format(new Date(), "EEEE, d MMMM yyyy")} · Dubai, UAE
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span
              className={`px-3 py-1 rounded-full text-xs font-semibold capitalize ${tierColors[tier]?.bg ?? ""} ${tierColors[tier]?.text ?? ""}`}
            >
              {tier}
            </span>
            <span className="font-mono text-2xl font-bold text-[hsl(var(--flexaura-accent))]">
              {availablePoints.toLocaleString()}
            </span>
            <span className="text-[hsl(0,0%,100%,0.5)] text-xs">pts</span>
          </div>
        </div>

        {/* Tier progress bar */}
        {tier !== "gold" && (
          <div className="mt-5">
            <div className="flex items-center justify-between text-xs text-[hsl(0,0%,100%,0.6)] mb-1.5">
              <span className="capitalize">{tier}</span>
              <span>{pointsToNext.toLocaleString()} points to {nextTierName}</span>
            </div>
            <div className="h-2 rounded-full bg-[hsl(0,0%,100%,0.1)] overflow-hidden">
              <div
                className="h-full rounded-full bg-[hsl(var(--flexaura-accent))] transition-all duration-700"
                style={{ width: `${tierProgress}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* ═══ 2. STATS ROW ═══ */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card className="card-premium">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Points</CardTitle>
            <Gift className="h-4 w-4 icon-muted" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-mono font-bold text-[hsl(var(--flexaura-accent))]">
              {availablePoints.toLocaleString()}
            </div>
          </CardContent>
        </Card>

        <Card className="card-premium">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Bookings</CardTitle>
            <Calendar className="h-4 w-4 icon-muted" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-mono font-bold text-[hsl(var(--flexaura-primary))]">
              {bookings.filter((b) => b.status === "confirmed" || b.status === "completed").length}
            </div>
          </CardContent>
        </Card>

        <Card className="card-premium">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Financed</CardTitle>
            <CreditCard className="h-4 w-4 icon-muted" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-mono font-bold text-[hsl(var(--flexaura-primary))]">
              AED {totalFinanced.toLocaleString()}
            </div>
          </CardContent>
        </Card>

        {expiringIn30 > 0 ? (
          <Card className="card-premium border-[hsl(var(--warning))]">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Expiring in 30 Days</CardTitle>
              <AlertTriangle className="h-4 w-4 text-[hsl(var(--warning))]" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-mono font-bold text-[hsl(var(--warning))]">
                {expiringIn30.toLocaleString()}
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="card-premium">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Referrals Made</CardTitle>
              <Users className="h-4 w-4 icon-muted" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-mono font-bold text-[hsl(var(--flexaura-primary))]">
                {referralCount}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* ═══ 3. BOOKING STATUS TRACKER ═══ */}
      <section>
        <h2 className="text-xl font-heading mb-4">Your Bookings</h2>
        {bookings.length === 0 ? (
          <Card className="card-premium">
            <CardContent className="py-12 text-center space-y-4">
              <p className="text-lg font-medium">Your bookings will appear here. Ready to start?</p>
              <div className="flex flex-wrap justify-center gap-3">
                <Button asChild variant="outline" size="sm">
                  <Link to="/travel"><Plane className="mr-1.5 h-4 w-4" />Explore Travel</Link>
                </Button>
                <Button asChild variant="outline" size="sm">
                  <Link to="/education"><GraduationCap className="mr-1.5 h-4 w-4" />Explore Education</Link>
                </Button>
                <Button asChild variant="outline" size="sm">
                  <Link to="/healthcare"><Heart className="mr-1.5 h-4 w-4" />Explore Healthcare</Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {bookings.map((b) => (
              <Card key={b.id} className="card-premium">
                <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                  <span className="text-xl">
                    {verticalIcon.travel}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{b.offer_id}</p>
                    <p className="text-xs text-muted-foreground font-mono">
                      {b.created_at ? format(parseISO(b.created_at), "d MMM yyyy") : "—"}
                    </p>
                  </div>
                  <div className="font-mono font-semibold text-[hsl(var(--flexaura-primary))]">
                    AED {Number(b.total_amount_aed).toLocaleString()}
                  </div>
                  <span
                    className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize ${statusPill[b.status] ?? statusPill.pending}`}
                  >
                    {b.status}
                  </span>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </section>

      {/* ═══ 4. POINTS TRANSACTION HISTORY ═══ */}
      <section>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <h2 className="text-xl font-heading">Points Activity</h2>
          {availablePoints >= 500 ? (
            <Dialog open={redeemOpen} onOpenChange={setRedeemOpen}>
              <DialogTrigger asChild>
                <Button size="sm" className="gap-1.5">
                  <TrendingUp className="h-4 w-4" />
                  Redeem Points
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="font-heading">Redeem Points</DialogTitle>
                </DialogHeader>
                <div className="space-y-6 py-4">
                  <p className="text-sm text-muted-foreground">
                    Current balance: <span className="font-mono font-semibold text-[hsl(var(--flexaura-accent))]">{availablePoints.toLocaleString()}</span>
                  </p>
                  <div>
                    <Label className="text-sm mb-2 block">Points to redeem</Label>
                    <Slider
                      min={500}
                      max={Math.min(availablePoints, 50000)}
                      step={100}
                      value={[redeemAmount]}
                      onValueChange={([v]) => setRedeemAmount(v)}
                    />
                    <div className="flex justify-between mt-2 text-sm">
                      <span className="font-mono">{redeemAmount.toLocaleString()} pts</span>
                      <span className="font-mono text-[hsl(var(--flexaura-accent))]">
                        = AED {(redeemAmount / 100).toFixed(2)}
                      </span>
                    </div>
                  </div>
                  <Button className="w-full" onClick={handleRedeem}>
                    Apply to next booking <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          ) : (
            <p className="text-sm text-muted-foreground">
              You need <span className="font-mono font-semibold">{(500 - availablePoints).toLocaleString()}</span> more points to redeem. Keep booking!
            </p>
          )}
        </div>

        {/* Filter pills */}
        <div className="flex flex-wrap gap-2 mb-4">
          {(["all", "earn", "redeem", "expire", "referral"] as PointsFilter[]).map((f) => (
            <button
              key={f}
              onClick={() => setPointsFilter(f)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors capitalize ${
                pointsFilter === f
                  ? "bg-[hsl(var(--flexaura-primary))] text-[hsl(0,0%,100%)]"
                  : "bg-[hsl(var(--flexaura-bg))] text-muted-foreground hover:bg-border"
              }`}
            >
              {f === "all" ? "All" : f === "earn" ? "Earned" : f === "redeem" ? "Redeemed" : f === "expire" ? "Expiring" : "Referrals"}
            </button>
          ))}
        </div>

        <Card className="card-premium overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-muted-foreground text-left">
                  <th className="px-4 py-3 font-medium">Date</th>
                  <th className="px-4 py-3 font-medium">Description</th>
                  <th className="px-4 py-3 font-medium text-right">Points</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Expires</th>
                </tr>
              </thead>
              <tbody>
                {filteredLedger.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                      No activity found
                    </td>
                  </tr>
                ) : (
                  filteredLedger.map((r) => (
                    <tr key={r.id} className="border-b last:border-0 hover:bg-muted/30">
                      <td className="px-4 py-3 font-mono text-xs">{format(parseISO(r.created_at), "d MMM yyyy")}</td>
                      <td className="px-4 py-3">{r.description || r.kind}</td>
                      <td className={`px-4 py-3 font-mono font-semibold text-right ${r.points_sign === 1 ? "text-[hsl(var(--flexaura-success))]" : "text-[hsl(var(--flexaura-error))]"}`}>
                        {r.points_sign === 1 ? "+" : "−"}{r.points.toLocaleString()}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold capitalize ${
                          r.kind === "earn" ? "bg-[hsl(var(--flexaura-success)/0.15)] text-[hsl(var(--flexaura-success))]" :
                          r.kind === "redeem" ? "bg-[hsl(var(--flexaura-primary)/0.1)] text-[hsl(var(--flexaura-primary))]" :
                          r.kind === "expire" ? "bg-[hsl(var(--warning)/0.15)] text-[hsl(var(--warning))]" :
                          "bg-muted text-muted-foreground"
                        }`}>
                          {r.kind}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                        {r.expires_at ? format(parseISO(r.expires_at), "d MMM yyyy") : "—"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </section>

      {/* ═══ 5. SAVED PREFERENCES ═══ */}
      <section>
        <h2 className="text-xl font-heading mb-4">Your Preferences</h2>
        <Card className="card-premium">
          <CardContent className="p-6 space-y-6">
            {/* Preferred BNPL Provider */}
            <div className="space-y-2">
              <Label className="font-medium">Preferred BNPL Provider</Label>
              <Select
                value={preferredBnpl ?? "none"}
                onValueChange={(v) => {
                  const val = v === "none" ? null : v;
                  setPreferredBnpl(val);
                  savePreference("preferred_bnpl_provider", val);
                }}
              >
                <SelectTrigger className="w-full max-w-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No preference</SelectItem>
                  <SelectItem value="tabby">Tabby</SelectItem>
                  <SelectItem value="tamara">Tamara</SelectItem>
                  <SelectItem value="cashew">Cashew</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">We'll show this provider first at checkout</p>
            </div>

            {/* Notification Preferences */}
            <div className="space-y-4">
              <Label className="font-medium">Notification Preferences</Label>
              <div className="flex items-center justify-between max-w-xs">
                <Label htmlFor="notify-earned" className="text-sm">Email me when I earn points</Label>
                <Switch
                  id="notify-earned"
                  checked={notifyEarned}
                  onCheckedChange={(v) => {
                    setNotifyEarned(v);
                    savePreference("notify_points_earned", v);
                  }}
                />
              </div>
              <div className="flex items-center justify-between max-w-xs">
                <Label htmlFor="notify-expiring" className="text-sm">Email me when points are expiring</Label>
                <Switch
                  id="notify-expiring"
                  checked={notifyExpiring}
                  onCheckedChange={(v) => {
                    setNotifyExpiring(v);
                    savePreference("notify_points_expiring", v);
                  }}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* ═══ 6. REFERRAL CARD ═══ */}
      <section>
        <h2 className="text-xl font-heading mb-4">Refer & Earn</h2>
        <ReferralShare />
      </section>
    </div>
  );
}
